import { useEffect, useState, useMemo } from "react";
import { Link, useLocation } from "wouter";
import { Filter, ArrowRight, ListTodo, Plus, Upload, MessageCircle, AlertTriangle, Clock, Inbox, CheckSquare } from "lucide-react";
import BankerUploadArtifactModal from "../../components/banker-upload-artifact-modal";
import BankerNewTaskModal from "../../components/banker-new-task-modal";
import { WheelhouseBadge } from "../../components/wheelhouse-badge";
import { useActiveCohort, withCohort } from "@/lib/cohort-context";

const BASE = import.meta.env.BASE_URL;
const SLA_HOURS = 48; // SLA breach threshold for open tasks

export default function BankerTasks() {
  const [, navigate] = useLocation();
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("open");
  const [scope, setScope] = useState<"mine" | "all">("all");
  const [showUpload, setShowUpload] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [claimingId, setClaimingId] = useState<number | null>(null);
  const [me, setMe] = useState<any>(null);
  const { cohortId } = useActiveCohort();

  const load = async () => {
    const token = localStorage.getItem("banker_token");
    if (!token) return;
    setLoading(true);
    try {
      const [r, rMe] = await Promise.all([
        fetch(withCohort(`${BASE}api/banker/tasks?scope=${scope}`, cohortId), { headers: { "x-banker-token": token } }),
        me ? Promise.resolve(null) : fetch(`${BASE}api/bankers/me`, { headers: { "x-banker-token": token } }),
      ]);
      if (r.ok) setTasks(await r.json());
      if (rMe && rMe.ok) setMe(await rMe.json());
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, cohortId]);

  const health = useMemo(() => {
    const now = Date.now();
    // "Open" = needs banker action. Both terminal states (closed, dismissed)
    // are excluded so dismissed tasks don't pollute SLA risk and unclaimed
    // counts. "reopened" stays open — that's the whole point of the loop.
    const isOpen = (s: string) => s !== "closed" && s !== "dismissed";
    const slaRisk = tasks.filter(t => t.ownable && isOpen(t.status) && (now - new Date(t.createdAt).getTime()) / 3600000 > SLA_HOURS);
    const unclaimed = tasks.filter(t => t.ownable && !t.bankerId && isOpen(t.status));
    const mineOpen = me ? tasks.filter(t => t.bankerId === me.id && isOpen(t.status) && t.status !== "awaiting_founder") : [];
    const awaitingFounder = tasks.filter(t => t.ownable && t.status === "awaiting_founder");
    return { slaRisk, unclaimed, mineOpen, awaitingFounder };
  }, [tasks, me]);

  const filtered = tasks.filter(t => {
    if (filter === "open") return t.status !== "closed" && t.status !== "dismissed";
    if (filter === "sla_risk") return health.slaRisk.includes(t);
    if (filter === "unclaimed") return health.unclaimed.includes(t);
    if (filter === "mine_open") return health.mineOpen.includes(t);
    if (filter === "awaiting_founder_mine") return health.awaitingFounder.includes(t);
    if (filter === "all") return true;
    return t.status === filter;
  });

  const claim = async (id: number) => {
    setClaimingId(id);
    const token = localStorage.getItem("banker_token");
    try {
      const r = await fetch(`${BASE}api/banker/tasks/${id}/claim`, { method: "POST", headers: { "x-banker-token": token || "" } });
      if (r.ok) await load();
    } catch {}
    setClaimingId(null);
  };

  const priorityColor: any = { high: "text-red-400 bg-red-500/15 border-red-500/30", medium: "text-amber-400 bg-amber-500/15 border-amber-500/30", low: "text-white/50 bg-white/5 border-white/10" };
  const statusColor: any = { pending: "text-blue-400", in_progress: "text-amber-400", awaiting_founder: "text-purple-400", artifact_uploaded: "text-emerald-400", closed: "text-white/40", reopened: "text-amber-300", dismissed: "text-white/30" };

  const hoursAgo = (d: string) => Math.round((Date.now() - new Date(d).getTime()) / 3600000);

  return (
    <div className="space-y-5">
      {/* Header + primary CTAs */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><ListTodo className="w-7 h-7 text-[#FF5C00]" /> Tasks</h1>
          <p className="text-white/50 text-sm mt-1">Full desk visibility. You can only act on tasks within your portfolio coverage.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowNew(true)} className="bg-[#FF5C00] hover:bg-[#E55200] text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
            <Plus className="w-4 h-4" /> Log task
          </button>
          <button onClick={() => setShowUpload(true)} className="bg-white/[0.08] hover:bg-white/[0.12] text-white border border-white/15 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
            <Upload className="w-4 h-4" /> Upload artifact
          </button>
          <Link href="/banker/meera">
            <a className="bg-white/[0.04] hover:bg-white/[0.08] text-white/80 border border-white/10 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
              <MessageCircle className="w-4 h-4" /> Ask Meera
            </a>
          </Link>
        </div>
      </div>

      {/* Queue Health strip — clickable filters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <HealthTile active={filter === "sla_risk"} onClick={() => setFilter(filter === "sla_risk" ? "open" : "sla_risk")}
          icon={<AlertTriangle className="w-4 h-4" />} label="SLA risk" count={health.slaRisk.length} tone="red" sub={`>${SLA_HOURS}h open`} />
        <HealthTile active={filter === "unclaimed"} onClick={() => setFilter(filter === "unclaimed" ? "open" : "unclaimed")}
          icon={<Inbox className="w-4 h-4" />} label="Unclaimed in coverage" count={health.unclaimed.length} tone="orange" sub="Ready to grab" />
        <HealthTile active={filter === "mine_open"} onClick={() => setFilter(filter === "mine_open" ? "open" : "mine_open")}
          icon={<CheckSquare className="w-4 h-4" />} label="In your hands" count={health.mineOpen.length} tone="emerald" sub="Your active work" />
        <HealthTile active={filter === "awaiting_founder_mine"} onClick={() => setFilter(filter === "awaiting_founder_mine" ? "open" : "awaiting_founder_mine")}
          icon={<Clock className="w-4 h-4" />} label="Awaiting founder" count={health.awaitingFounder.length} tone="purple" sub="Blocked on reply" />
      </div>

      {/* Scope + status filters */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap text-xs">
          <Filter className="w-3.5 h-3.5 text-white/40 mr-1" />
          {[
            { k: "open", label: "Open" },
            { k: "pending", label: "Pending" },
            { k: "in_progress", label: "In Progress" },
            { k: "awaiting_founder", label: "Awaiting Founder" },
            { k: "artifact_uploaded", label: "Artifact Uploaded" },
            { k: "closed", label: "Closed" },
            { k: "all", label: "All" },
          ].map(f => (
            <button key={f.k} onClick={() => setFilter(f.k)} className={`px-3 py-1.5 rounded-full border transition-colors ${filter === f.k ? "bg-[#FF5C00] border-[#FF5C00] text-white" : "bg-white/[0.04] border-white/[0.1] text-white/60 hover:text-white hover:border-white/[0.2]"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center bg-white/[0.04] border border-white/[0.1] rounded-lg p-0.5 text-xs">
          <button onClick={() => setScope("all")} className={`px-3 py-1.5 rounded ${scope === "all" ? "bg-[#FF5C00] text-white" : "text-white/60"}`}>Everywhere</button>
          <button onClick={() => setScope("mine")} className={`px-3 py-1.5 rounded ${scope === "mine" ? "bg-[#FF5C00] text-white" : "text-white/60"}`}>Actionable by me</button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-white/[0.04] border border-white/[0.08] rounded-lg animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <EmptyState onNew={() => setShowNew(true)} onUpload={() => setShowUpload(true)} totalTasks={tasks.length} filter={filter} />
      ) : (
        <div className="space-y-2">
          {filtered.map((t) => {
            const stale = t.ownable && t.status !== "closed" && hoursAgo(t.createdAt) > SLA_HOURS;
            return (
              <div key={t.id} className="bg-white/[0.04] hover:bg-white/[0.06] border border-white/[0.08] hover:border-white/[0.15] rounded-xl p-5 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <Link href={`/banker/tasks/${t.id}`} asChild>
                    <a className="flex-1 min-w-0 block">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className={`text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border ${priorityColor[t.priority] || priorityColor.medium}`}>{t.priority}</span>
                        <span className={`text-[10px] uppercase tracking-wider ${statusColor[t.status]}`}>{t.status.replace("_", " ")}</span>
                        <span className="text-[10px] text-white/30 font-mono">{t.category}</span>
                        {t.confidence && t.confidence !== "high" && (
                          <span
                            title={t.confidence === "low"
                              ? "Meera was not certain this was actionable. Verify the investor's intent before reaching out."
                              : "Meera had medium confidence in this extraction. Skim the source chat before acting."}
                            className={`text-[10px] px-1.5 py-0.5 rounded font-mono border ${
                              t.confidence === "low"
                                ? "bg-yellow-500/15 text-yellow-400 border-yellow-500/30"
                                : "bg-white/5 text-white/50 border-white/10"
                            }`}
                          >
                            {t.confidence === "low" ? "Meera unsure" : "Meera medium-conf"}
                          </span>
                        )}
                        {stale && <span className="text-[10px] bg-red-500/15 text-red-400 border border-red-500/30 px-1.5 py-0.5 rounded font-mono flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5" /> {hoursAgo(t.createdAt)}h</span>}
                        {t.companyId && <WheelhouseBadge status={t.ownable ? "in_wheelhouse" : "other_portco"} size="xs" showLabel={false} />}
                        {!t.bankerId && t.ownable && <span className="text-[10px] bg-[#FF5C00]/20 text-[#FF5C00] px-1.5 py-0.5 rounded">Unclaimed</span>}
                        {t.bankerId && me && t.bankerId === me.id && <span className="text-[10px] bg-emerald-500/15 text-emerald-400 px-1.5 py-0.5 rounded">Yours</span>}
                      </div>
                      <p className="text-white text-sm font-medium">{t.title}</p>
                      {t.investorIntent && <p className="text-white/50 text-xs mt-1.5 leading-relaxed line-clamp-2">{t.investorIntent}</p>}
                      <p className="text-white/40 text-xs mt-2">
                        {t.investor?.name || "—"}
                        {t.company ? <> · <span className="text-white/60">{t.company.name}</span></> : null}
                        <span className="mx-2">·</span>
                        {new Date(t.createdAt).toLocaleDateString()}
                      </p>
                    </a>
                  </Link>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {!t.bankerId && t.ownable && (
                      <button onClick={() => claim(t.id)} disabled={claimingId === t.id}
                        className="bg-[#FF5C00] hover:bg-[#E55200] text-white text-xs px-3 py-1.5 rounded-lg font-medium whitespace-nowrap disabled:opacity-50">
                        {claimingId === t.id ? "Claiming..." : "Claim"}
                      </button>
                    )}
                    <Link href={`/banker/tasks/${t.id}`}>
                      <a className="text-white/40 hover:text-white"><ArrowRight className="w-4 h-4" /></a>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showUpload && <BankerUploadArtifactModal onClose={() => setShowUpload(false)} onUploaded={load} />}
      {showNew && <BankerNewTaskModal onClose={() => setShowNew(false)} onCreated={(id) => navigate(`/banker/tasks/${id}`)} />}
    </div>
  );
}

function HealthTile({ icon, label, count, sub, tone, active, onClick }: {
  icon: React.ReactNode; label: string; count: number; sub: string;
  tone: "red" | "orange" | "emerald" | "purple"; active: boolean; onClick: () => void;
}) {
  const tones: Record<string, string> = {
    red: "text-red-400 border-red-500/25 hover:border-red-500/50 bg-red-500/[0.04]",
    orange: "text-[#FF5C00] border-[#FF5C00]/25 hover:border-[#FF5C00]/50 bg-[#FF5C00]/[0.04]",
    emerald: "text-emerald-400 border-emerald-500/25 hover:border-emerald-500/50 bg-emerald-500/[0.04]",
    purple: "text-purple-400 border-purple-500/25 hover:border-purple-500/50 bg-purple-500/[0.04]",
  };
  const activeRing: Record<string, string> = {
    red: "ring-red-500/40", orange: "ring-[#FF5C00]/40", emerald: "ring-emerald-500/40", purple: "ring-purple-500/40",
  };
  return (
    <button onClick={onClick} className={`text-left rounded-xl border p-3.5 transition-all ${tones[tone]} ${active ? `ring-2 ${activeRing[tone]}` : ""}`}>
      <div className="flex items-center justify-between">
        <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-mono font-semibold`}>{icon} {label}</div>
        <span className="text-2xl font-bold text-white">{count}</span>
      </div>
      <p className="text-[10px] text-white/40 mt-1">{sub}</p>
    </button>
  );
}

function EmptyState({ onNew, onUpload, totalTasks, filter }: { onNew: () => void; onUpload: () => void; totalTasks: number; filter: string }) {
  const isFilterView = totalTasks > 0 && filter !== "open" && filter !== "all";
  return (
    <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-10">
      <div className="max-w-2xl mx-auto text-center mb-8">
        <div className="inline-flex w-14 h-14 rounded-2xl bg-[#FF5C00]/15 border border-[#FF5C00]/25 items-center justify-center mb-4">
          <ListTodo className="w-7 h-7 text-[#FF5C00]" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">
          {isFilterView ? "Nothing in this view." : "A quiet desk."}
        </h2>
        <p className="text-white/55 text-sm leading-relaxed">
          {isFilterView
            ? "Try clearing the filter, or use the actions below to get ahead of the day."
            : "No investor asks have landed here yet. Get proactive — log the work you're already doing or drop in founder context so Meera can use it."}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-3xl mx-auto">
        <button onClick={onNew}
          className="text-left bg-white/[0.04] hover:bg-[#FF5C00]/[0.08] border border-white/[0.1] hover:border-[#FF5C00]/40 rounded-xl p-5 transition-all">
          <Plus className="w-5 h-5 text-[#FF5C00] mb-3" />
          <p className="text-white font-semibold text-sm mb-1">Log a task</p>
          <p className="text-white/50 text-xs leading-relaxed">Capture outbound work you're about to do — intros, diligence, follow-ups.</p>
        </button>
        <button onClick={onUpload}
          className="text-left bg-white/[0.04] hover:bg-[#FF5C00]/[0.08] border border-white/[0.1] hover:border-[#FF5C00]/40 rounded-xl p-5 transition-all">
          <Upload className="w-5 h-5 text-[#FF5C00] mb-3" />
          <p className="text-white font-semibold text-sm mb-1">Upload artifact</p>
          <p className="text-white/50 text-xs leading-relaxed">Drop a Zoom transcript, email thread, or deck. Meera summarises for the record.</p>
        </button>
        <Link href="/banker/meera">
          <a className="text-left bg-white/[0.04] hover:bg-[#FF5C00]/[0.08] border border-white/[0.1] hover:border-[#FF5C00]/40 rounded-xl p-5 transition-all block">
            <MessageCircle className="w-5 h-5 text-[#FF5C00] mb-3" />
            <p className="text-white font-semibold text-sm mb-1">Ask Meera</p>
            <p className="text-white/50 text-xs leading-relaxed">Get a live read on any investor, company, or task in your coverage.</p>
          </a>
        </Link>
      </div>
    </div>
  );
}
